import { TemplateRef } from '@angular/core';

import {
  CardTemplateContext,
  CellTemplateContext,
  ColumnBuilder,
  CustomRowContext,
  DatasetTableConfig,
  formattedColumn,
  TableConfigBuilder,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { TestCamerasFlatRow, TestCamerasRowType } from 'src/app/cameras/camera.model';

export type TestCameraTableCellContext = CellTemplateContext<TestCamerasFlatRow>;

export interface CreateTableConfigDependencies {
  edgeHeaderTemplate: TemplateRef<CustomRowContext<TestCamerasFlatRow>>;
  cameraCellTemplate: TemplateRef<TestCameraTableCellContext>;
  mainBitrateCellTemplate: TemplateRef<TestCameraTableCellContext>;
  mainDropCellTemplate: TemplateRef<TestCameraTableCellContext>;
  subBitrateCellTemplate: TemplateRef<TestCameraTableCellContext>;
  subDropCellTemplate: TemplateRef<TestCameraTableCellContext>;
  canEditCamera: (cameraId: string) => boolean;
  customCardRowTemplate?: (row: TestCamerasFlatRow) => TemplateRef<CardTemplateContext<TestCamerasFlatRow>> | null;
}

const LABELS = {
  CAMERA: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.CAMERA',
  MAIN_STREAM: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.MAIN_STREAM',
  SUB_STREAM: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.SUB_STREAM',
  RESOLUTION: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.RESOLUTION',
  FPS: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.FPS',
  CODEC: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.CODEC',
  BITRATE: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.BITRATE',
  DROP: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.DROP',
  LOAD: 'TEST_SELECTED_CAMERA_DIALOG.TABLE.LOAD',
} as const;

function getCameraRowValue<T>(
  row: TestCamerasFlatRow,
  getter: (row: Extract<TestCamerasFlatRow, { type: TestCamerasRowType.Camera }>) => T,
): T | string {
  if (row.type !== TestCamerasRowType.Camera) {
    return '—';
  }
  return getter(row);
}

export function createTableConfig(
  deps: CreateTableConfigDependencies,
): DatasetTableConfig<TestCamerasFlatRow> {
  const builder = new ColumnBuilder<TestCamerasFlatRow>();

  return new TableConfigBuilder<TestCamerasFlatRow>()
    .columns([
      builder
        .column('cameraId' as keyof TestCamerasFlatRow)
        .label(LABELS.CAMERA)
        .width('minmax(160px, 1fr)')
        .cellTemplate(deps.cameraCellTemplate)
        .mobileVisible()
        .cardIcon('ui-icon-live-cam')
        .build(),

      formattedColumn<TestCamerasFlatRow>(
        'mainStream' as keyof TestCamerasFlatRow,
        LABELS.RESOLUTION,
        (_, row) => getCameraRowValue(row, (r) => r.mainStream?.resolution ?? '—'),
        { width: 'minmax(85px, 0.65fr)', mobileVisible: true, cardIcon: 'ui-icon-image' },
      ),
      formattedColumn<TestCamerasFlatRow>(
        'mainStream' as keyof TestCamerasFlatRow,
        LABELS.FPS,
        (_, row) =>
          getCameraRowValue(row, (r) =>
            r.mainStream?.fps != null ? String(r.mainStream.fps) : '—',
          ),
        { width: 'minmax(56px, 0.5fr)', mobileVisible: true, cardIcon: 'ui-icon-film' },
      ),
      formattedColumn<TestCamerasFlatRow>(
        'mainStream' as keyof TestCamerasFlatRow,
        LABELS.CODEC,
        (_, row) => getCameraRowValue(row, (r) => r.mainStream?.codec ?? '—'),
        { width: 'minmax(60px, 0.45fr)' },
      ),
      builder
        .column('mainStream' as keyof TestCamerasFlatRow)
        .label(LABELS.BITRATE)
        .width('minmax(80px, 0.7fr)')
        .cellTemplate(deps.mainBitrateCellTemplate)
        .build(),
      builder
        .column('mainStream' as keyof TestCamerasFlatRow)
        .label(LABELS.DROP)
        .width('minmax(60px, 0.5fr)')
        .cellTemplate(deps.mainDropCellTemplate)
        .mobileVisible()
        .build(),

      formattedColumn<TestCamerasFlatRow>(
        'substream' as keyof TestCamerasFlatRow,
        LABELS.RESOLUTION,
        (_, row) => getCameraRowValue(row, (r) => r.substream?.resolution ?? '—'),
        { width: 'minmax(85px, 0.65fr)' },
      ),
      formattedColumn<TestCamerasFlatRow>(
        'substream' as keyof TestCamerasFlatRow,
        LABELS.FPS,
        (_, row) =>
          getCameraRowValue(row, (r) =>
            r.substream?.fps != null ? String(r.substream.fps) : '—',
          ),
        { width: 'minmax(56px, 0.5fr)' },
      ),
      formattedColumn<TestCamerasFlatRow>(
        'substream' as keyof TestCamerasFlatRow,
        LABELS.CODEC,
        (_, row) =>
          getCameraRowValue(row, (r) => {
            if (!r.substream?.resolution && !r.substream?.fps) return '—';
            return r.substream?.codec ?? '—';
          }),
        { width: 'minmax(60px, 0.45fr)' },
      ),
      builder
        .column('substream' as keyof TestCamerasFlatRow)
        .label(LABELS.BITRATE)
        .width('minmax(80px, 0.7fr)')
        .cellTemplate(deps.subBitrateCellTemplate)
        .build(),
      builder
        .column('substream' as keyof TestCamerasFlatRow)
        .label(LABELS.DROP)
        .width('minmax(60px, 0.5fr)')
        .cellTemplate(deps.subDropCellTemplate)
        .build(),

      formattedColumn<TestCamerasFlatRow>(
        'load' as keyof TestCamerasFlatRow,
        LABELS.LOAD,
        (v, row) =>
          row.type === TestCamerasRowType.Camera && v != null
            ? `${Number(v).toFixed(0)} %`
            : '—',
        {
          width: 'minmax(56px, 0.45fr)',
          mobileVisible: true,
          cardIcon: 'ui-icon-bar-chart',
          alignCenter: true,
        },
      ),
    ])
    .actions([
      {
        id: 'edit',
        icon: 'ui-icon-edit',
        tooltip: 'COMMON.EDIT',
        visible: (row) => {
          if (row.type !== TestCamerasRowType.Camera) return false;
          return deps.canEditCamera(row.cameraId);
        },
        showInline: true,
        alwaysVisible: true,
      },
    ])
    .trackBy('cameraId' as keyof TestCamerasFlatRow)
    .rowHeight(56)
    .customRowRenderer((row) => {
      if (row.type === TestCamerasRowType.Edge) {
        return deps.edgeHeaderTemplate;
      }
      return null;
    })
    .customRowFullWidth(true)
    .hideTableHeader(true)
    .extend({
      responsive: {
        breakpoints: { mobile: 1200 },
        ...(deps.customCardRowTemplate && {
          cardLayout: { customCardRowTemplate: deps.customCardRowTemplate },
        }),
      },
    })
    .build();
}
